<template>
  <RelationshipProvider>
    <div
      class="flex flex-col flex-1 w-full relation-tab"
      :class="{ fullScreen }"
    >
      <FlotoContentLoader :loading="loading">
        <MRow>
          <MCol :size="12" class="flex justify-end">
            <ExportButton
              v-if="(loadedRelations || []).length"
              :button-text="$t('download')"
              class="mx-1"
              :header="`${$t('download')}`"
              :file-name="$tc(subModuleName, 2)"
              allow-pdf
              :options="[
                { text: $t('pdf'), value: 'pdf' },
                { text: $t('excel'), value: 'excel' },
                { text: $t('csv'), value: 'csv' },
              ]"
              :default-data="{ exportFormat: 'pdf' }"
              :download-fn="handleDownloadAssetDepreciationDetails"
            />
            <MButton
              variant="primary"
              :shadow="false"
              class="mx-2"
              @click="fullScreen = !fullScreen"
            >
              <MIcon name="expand-arrows" />
            </MButton>
            <FlotoUserDrawer
              v-if="canViewNode && activeNode.model === 'user'"
              :key="index"
              :user-id="activeNode.resourceId"
            >
              <template v-slot="{ open }">
                <MButton class="mr-2" @click="open">
                  {{ $tc('view') }} {{ activeNode.name }}
                </MButton>
              </template>
            </FlotoUserDrawer>
            <MButton
              v-if="canViewNode && activeNode.model !== 'user'"
              class="mr-2"
              @click="viewAsset"
            >
              {{ $tc('view') }} {{ activeNode.name }}
            </MButton>
            <MButton
              v-if="canAddOrViewRelationship && !disabled"
              class="mx-2"
              @click="showAddRelationshipForm = true"
            >
              {{ $tc('add') }} {{ __tc('relationship') }} {{ $t('to') }}
              {{ activeNode.name }}
            </MButton>
            <MButton
              v-if="canAddOrViewRelationship"
              @click="viewActiveNodesRelations"
            >
              {{ $tc('view') }} {{ activeNode.name }}
              {{ __tc('relationship', 2) }}
            </MButton>
          </MCol>
        </MRow>
        <MRow class="flex-1">
          <MCol :size="12" class="flex">
            <RelationshipGraph
              :fullscreen="fullScreen"
              :relations="loadedRelations"
              :source="{ ...resource, model: subModuleName, assetIconName }"
              :module-name="moduleName"
              @click="activeNode = $event"
            />
          </MCol>
        </MRow>
        <AddRelationshipForm
          v-if="showAddRelationshipForm"
          :create-fn="handleAddRelation"
          :resource="activeNode"
          :excluded-ids-map="excludedIdsMap"
          @hide="showAddRelationshipForm = false"
          @target-type-changed="handleTargetTypeChanged"
        />
        <ViewRelationships
          v-if="showRelationOfActiveNode"
          :source="activeNode"
          :module-name="moduleName"
          @remove="getRelations"
          @hide="showRelationOfActiveNode = false"
        />
      </FlotoContentLoader>
    </div>
  </RelationshipProvider>
</template>

<script>
import ExportButton from '@modules/reports/components/export-button'
import { findValueObject } from '@data/recursive'
import { OrganizationComputed } from '@state/modules/organization'
import { AssetTypeComputed } from '@state/modules/asset-type'
import { CITypeComputed } from '@state/modules/ci-type'
import { getEncryptedPassword } from '@utils/password'
import { transformSearchCriteriasForServer } from '@data/search'
import AddRelationshipForm from './add-relationship-form'
import RelationshipProvider from './relationship-provider'
import ViewRelationships from './view-relationships'
import RelationshipGraph from './relationship-graph'
import {
  getAssetRelationshipsAsTreeApi,
  createAssetRelationshipBulkApi,
  downloadAssetRelationApi,
} from '../../asset-api'
import { customFieldAttributesTypePrefix } from '@src/modules/asset/helpers/asset-custom-fields'

export default {
  name: 'RelationShip',
  components: {
    RelationshipProvider,
    AddRelationshipForm,
    ViewRelationships,
    RelationshipGraph,
    ExportButton,
  },
  props: {
    resource: { type: Object, required: true },
    moduleName: { type: String, required: true },
    subModuleName: { type: String, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      showAddRelationshipForm: false,
      showRelationOfActiveNode: false,
      fullScreen: false,
      viewType: 'list',
      loadedRelations: [],
      excludedIdsMap: {
        asset_hardware: [],
        asset_software: [],
        asset_non_it: [],
        asset_consumable: [],
        department: [],
        user_group: [],
        user: [],
      },
      activeNodeChanged: false,
      currentTargetType: undefined,
      assetIconName: null,
      activeNode: {
        resourceId: this.resource.id,
        model: this.subModuleName,
        name:
          this.moduleName === this.$constants.CMDB
            ? this.resource.ciName
            : this.resource.name,
        displayName:
          this.moduleName === this.$constants.CMDB
            ? this.resource.ciDisplayName
            : this.resource.displayName,
        assetTypeId:
          this.moduleName === this.$constants.CMDB
            ? this.resource.ciTypeId
            : this.resource.assetTypeId,
      },
    }
  },
  computed: {
    ...AssetTypeComputed,
    ...CITypeComputed,
    ...OrganizationComputed,
    canAddOrViewRelationship() {
      const activeNode = this.activeNode
      return (
        [
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_SOFTWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABlE,
          this.$constants.CMDB,
        ].indexOf(activeNode.model) >= 0
      )
    },
    canViewNode() {
      const activeNode = this.activeNode
      const source = this.resource
      if (
        activeNode.model === this.subModuleName &&
        activeNode.resourceId === source.id
      ) {
        return false
      }
      return (
        [
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_SOFTWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABlE,
          this.$constants.CMDB,
          'user',
        ].indexOf(activeNode.model) >= 0
      )
    },
    typyPrefix() {
      return customFieldAttributesTypePrefix(this.moduleName)
    },
  },
  watch: {
    activeNode: {
      immediate: true,
      handler(newValue, prevValue) {
        if (newValue !== prevValue) {
          this.resetOldExcludedIds()
          this.activeNodeChanged = true
          getAssetRelationshipsAsTreeApi(
            newValue.model,
            newValue.resourceId,
            this.moduleName,
            {
              treeview: true,
            }
          )
            .then((data) => {
              this.loading = false
              this.getExcludedIds(data)
            })
            .finally(() => {
              this.activeNodeChanged = false
            })
        }
      },
    },
  },
  created() {
    this.getRelations()
    const item =
      findValueObject(
        this[`${this.typyPrefix}Types`],
        this.resource[`${this.typyPrefix}TypeId`]
      ) || {}
    let assetIconName = 'otherIcon'
    if (item.systemName) {
      assetIconName = `${
        (item.systemName || '').split(' ').join('').toLowerCase() || ''
      }Icon`
    }
    if (item.iconSrc) {
      assetIconName = item.iconSrc
    }
    this.assetIconName = assetIconName
  },
  methods: {
    getAvailableAttachmentPassword(data) {
      const availableAttachmentPassword = data.protectedPassword
        ? getEncryptedPassword(data.protectedPassword)
        : getEncryptedPassword(this.attachmentPassword)
      return data.enabled ? availableAttachmentPassword : ''
    },
    handleDownloadAssetDepreciationDetails(data) {
      const requestData = {}
      requestData.model = this.subModuleName
      requestData.exportFormat = data.exportFormat
      requestData.enabled = data.enabled
      requestData.attachmentPassword = this.getAvailableAttachmentPassword(data)
      requestData.qualification = transformSearchCriteriasForServer(
        this.subModuleName,
        [
          {
            operator: { value: 'in' },
            paramName: 'id',
            value: [this.resource.id],
            valueType: 'long',
          },
          ...(this.resource.archived
            ? [
                {
                  operator: { value: 'equal' },
                  paramName: 'removed',
                  value: true,
                  valueType: 'boolean',
                },
                {
                  operator: { value: 'equal' },
                  paramName: 'removed',
                  value: false,
                  valueType: 'boolean',
                },
              ]
            : []),
        ]
      )
      requestData.columns = []
      requestData.type = 'ExportGridDataRest'
      requestData.assetDepreciationScheduleType = this.filterType
      return downloadAssetRelationApi(this.subModuleName, requestData).catch(
        (e) => e
      )
    },
    viewAsset() {
      const activeNode = this.activeNode
      if (
        [
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_SOFTWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABlE,
        ].indexOf(activeNode.model) >= 0
      ) {
        const route = this.$router.resolve(
          this.$modules.getModuleRoute('asset', 'view', {
            params: { id: activeNode.resourceId, assetType: activeNode.model },
          })
        )
        window.open(route.href, '_blank')
      }
      if ([this.$constants.CMDB].indexOf(activeNode.model) >= 0) {
        const route = this.$router.resolve(
          this.$modules.getModuleRoute('cmdb', 'view', {
            params: { id: activeNode.resourceId, ciType: activeNode.model },
          })
        )
        window.open(route.href, '_blank')
      }
    },
    getRelations(
      model = this.subModuleName,
      resourceId = this.resource.id,
      moduleName = this.moduleName,
      initial = false
    ) {
      this.resetOldExcludedIds()
      getAssetRelationshipsAsTreeApi(model, resourceId, moduleName, {
        treeview: true,
      }).then((data) => {
        this.loadedRelations = data
        this.loading = false
        this.getExcludedIds(data)
      })
    },
    viewActiveNodesRelations() {
      this.showRelationOfActiveNode = true
    },
    setActiveNode(guid) {
      this.activeNode = this.loadedRelations.find((r) => r.guid === guid)
    },
    handleAddRelation(data) {
      const d = {
        ...data,
        sourceId: this.activeNode.resourceId,
        sourceModel: this.activeNode.model,
      }
      const p = createAssetRelationshipBulkApi(d, this.moduleName)
      p.then(() => this.getRelations())
      return p
    },
    getExcludedIds(data) {
      data.forEach((obj) => {
        this.excludedIdsMap[obj.model].push(obj.resourceId)
      })
    },
    resetOldExcludedIds() {
      this.excludedIdsMap = {
        asset_hardware: [],
        asset_software: [],
        asset_non_it: [],
        asset_consumable: [],
        cmdb: [],
        department: [],
        user_group: [],
        user: [],
      }
    },
    handleTargetTypeChanged(targetType) {
      this.currentTargetType = targetType
      if (
        [
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_SOFTWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABlE,
          this.$constants.CMDB,
        ].indexOf(targetType) >= 0 &&
        this.activeNode.model === targetType
      ) {
        this.excludedIdsMap[targetType].push(this.resource.id)
      }
    },
  },
}
</script>

<style lang="less" scoped>
.relation-tab {
  &.fullScreen {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 100;
    padding: 20px;
    background: white;
  }
}
</style>
